import api from "@/app/api";
import Guid from "@/app/types/common/guid";
import { Instant, LocalDate, LocalTime } from "@js-joda/core";
import {ModelObject, BaseModelObject, ValidationRule} from "@/app/services/modelObject";
import ServiceBase from "@/app/services/serviceBase";
import converters from "@/app/services/converters"

// Model Objects

export class SearchQuery extends ModelObject<SearchQuery> {

    public query: string = "";

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.query = jsonSerializedObject.query;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.query = this.query;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
    ];
}


export class SearchResultItem extends ModelObject<SearchResultItem> {

    public type: string = "";
    public objectId: Guid = Guid.createEmpty();
    public description: string = "";
    public isHidden: boolean = false;

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.type = jsonSerializedObject.type;
          this.objectId = Guid.parseOrEmpty(jsonSerializedObject.objectId);
          this.description = jsonSerializedObject.description;
          this.isHidden = jsonSerializedObject.isHidden;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.type = this.type;
        toRet.objectId = this.objectId.toString();
        toRet.description = this.description;
        toRet.isHidden = this.isHidden;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "objectId", rule: (v: any) => !v.isEmpty() || "SearchResultItem.ObjectId cannot be empty"},
    ];
}


export class SearchResult extends ModelObject<SearchResult> {

    public searchResultItems: SearchResultItem[] = [];

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.searchResultItems = jsonSerializedObject.searchResultItems.map((v: any) => new SearchResultItem(v));
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.searchResultItems = this.searchResultItems.map(v => v.toJsonSerializedObject());
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "searchResultItems", rule: (v: any) => !!v || "searchResultItems is required"},
            {field: "searchResultItems", rule: (v: any) => {
                if (v === null) {
                    return true;
                }

                return (v as SearchResultItem[]).reduce((acc: Array<string>, curr: SearchResultItem) => acc.concat(curr.validate()), new Array<string>());
            }},
    ];
}


export class LiveUsesResult extends ModelObject<LiveUsesResult> {

    /**
    * Total sum of lengths of all Id arrays.
    */
    public totalCount: number = 0;
    /**
    * Ids of live stock items using the given item.
    */
    public stockItemIds: Guid[] = [];
    /**
    * Ids of live outlets using the given item.
    */
    public outletIds: Guid[] = [];
    /**
    * Ids of live suppliers and manufacturer using the given item.
    */
    public supplierManufacturerIds: Guid[] = [];

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.totalCount = jsonSerializedObject.totalCount;
          this.stockItemIds = jsonSerializedObject.stockItemIds.map((v: any) => Guid.parseOrEmpty(v));
          this.outletIds = jsonSerializedObject.outletIds.map((v: any) => Guid.parseOrEmpty(v));
          this.supplierManufacturerIds = jsonSerializedObject.supplierManufacturerIds.map((v: any) => Guid.parseOrEmpty(v));
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.totalCount = this.totalCount;
        toRet.stockItemIds = this.stockItemIds.map(v => v.toString());
        toRet.outletIds = this.outletIds.map(v => v.toString());
        toRet.supplierManufacturerIds = this.supplierManufacturerIds.map(v => v.toString());
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "totalCount", rule: (v: any) => v == null || v >= -2147483648 || "TotalCount is not greater than minimum allowed value: -2147483648"},
            {field: "totalCount", rule: (v: any) => v == null || v <= 2147483647 || "TotalCount is above maximum allowed value: 2147483647"},
    ];
}


export class SearchService extends ServiceBase {

  public async search(tenantId: Guid, searchQuery: SearchQuery, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<SearchResult> {
    const req = {
    tenantId:tenantId.toString(),
    searchQuery:searchQuery.toJsonSerializedObject(),
    };
    const extractor = (response:any) => new SearchResult(response.searchResult);;
    return this.makeJsonRequest("search/v1/search", req, false, extractor, opts);
  };
}

export const searchService = new SearchService();